import type { QuoteReadIdentity } from "@/api/quoteReadIdentity";
import type {
  QuoteChatWorksheetAccess,
  QuoteChatWorksheetLookup,
  RegisterQuoteChatAccess,
} from "./quoteChatWorkspaceAccess";
const same = (a: QuoteReadIdentity, b: QuoteReadIdentity) =>
  a.tenantId === b.tenantId && a.userId === b.userId && a.role === b.role;

/** One registry of live handles. The registration token differs from owner:
 * returning from a parked surface can reuse owner but must have a new lease.
 */
export function createQuoteChatRegistry(
  identity: () => QuoteReadIdentity | null,
  ready: () => boolean,
  changed: () => void = () => {},
) {
  const entries = new Map<
    object,
    { token: object; access: QuoteChatWorksheetAccess }
  >();
  const register: RegisterQuoteChatAccess = (access) => {
    const current = identity();
    if (!current || !same(access.identity, current)) return () => {};
    const token = {};
    entries.set(access.owner, { token, access });
    changed();
    return () => {
      if (entries.get(access.owner)?.token !== token) return;
      entries.delete(access.owner);
      changed();
    };
  };
  return {
    register,
    registered: (access: QuoteChatWorksheetAccess) =>
      entries.get(access.owner)?.access === access,
    size: () =>
      [...entries.values()].filter((e) => {
        const current = identity();
        return current && same(e.access.identity, current);
      }).length,
    clear: () => {
      entries.clear();
      changed();
    },
    lookup: (quoteId: string): QuoteChatWorksheetLookup => {
      const current = identity();
      if (!current || !ready()) return { kind: "blocked" };
      const found = [...entries.values()].filter(
        (e) => e.access.quoteId === quoteId && same(e.access.identity, current),
      );
      const evaluated = found.map((e) => ({ ...e, active: e.access.owns() }));
      const active = evaluated.filter((e) => e.active);
      if (active.length > 1) return { kind: "ambiguous" };
      if (evaluated.some((e) => !e.active)) return { kind: "blocked" };
      return active.length === 1
        ? { kind: "active", access: active[0].access }
        : { kind: "absent" };
    },
  };
}
